import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import {
  QUERY_GET_CURRENT_USER,
  getQueryPartnerAverageHealthScore,
  getQueryTypicalHealthScoreInRange,
  getQueryPartnerHealthScoreInRange,
  getAverageHealthScoreHeroQuery,
  getPartnerLatestStatsHeroQuery,
  getQueryTypicalAverageHealthScore,
  getPartnerDailyFactHeroQuery,
  getPartnerHealthScoreTimelineQuery,
  getTypicalHealthScoreTimelineQuery,
  getHourlyPartnerHealthScoreTimelineQuery,
  getHourlyTypicalHealthScoreTimelineQuery,
  getTypicalIssuesQuery,
  getPartnerIssuesQuery,
  getQueryHealthDownload,
  getQueryIssueMatrix,
} from './healthcheckQueries';
import { TimelineDataType } from './timelineProc';
import { HealthStatsType } from './healthBreakdownProc';
import { Maybe, Deployment, User } from '../../../types';
import { HealthScoreStat, IssueMatrixStat } from '../types';
import { isRhoOrCiSignal } from '../../../utils/environments';
import { PartnerHeroStats } from '../graphTypes';
import { useRecoilValue } from 'recoil';
import { activePartnerAtom } from '../../../state';

export const useGetCurrentUser = (): { loading: boolean; user: User } => {
  const { loading, error, data } = useQuery(QUERY_GET_CURRENT_USER);
  return useMemo(() => {
    if (error) {
      console.error('Error loading current user');
      return { loading: false, user: data?.me };
    }
    return {
      loading,
      user: data?.me,
    };
  }, [data, error, loading]);
};

export const useGetTypicalHealth = (): {
  typicalHealthLoading: boolean;
  typicalHealthArray: Maybe<HealthScoreStat>;
  deployment: Deployment;
} => {
  const activePartner = useRecoilValue(activePartnerAtom);
  const { loading, error, data } = useQuery(
    getQueryTypicalAverageHealthScore(),
    { variables: { partnerId: activePartner?.id } },
  );

  return useMemo(() => {
    if (error) {
      return {
        typicalHealthLoading: false,
        typicalHealthArray: undefined,
        deployment: data?.deployment,
      };
    }
    if (
      data?.signal_3_partner_stats_latest !== undefined &&
      data?.signal_3_partner_stats_latest.length === 0
    ) {
      return {
        typicalHealthLoading: false,
        typicalHealthArray: undefined,
        deployment: data?.deployment,
      };
    }
    return {
      typicalHealthLoading: loading,
      typicalHealthArray:
        data?.signal_3_global_stats_latest?.[0]?.health_score_distribution,
      deployment: data?.deployment,
    };
  }, [data, error, loading]);
};

export const useGetPartnerHealth = (
  partnerId: string,
): {
  partnerHealthLoading: boolean;
  partnerHealthArray: Maybe<HealthScoreStat>;
  deployment: Deployment;
} => {
  const { loading, error, data } = useQuery(
    getQueryPartnerAverageHealthScore(partnerId),
    { skip: !partnerId },
  );

  return useMemo(() => {
    if (error) {
      return {
        partnerHealthLoading: false,
        partnerHealthArray: undefined,
        deployment: data?.deployment,
      };
    }
    if (
      data?.signal_3_partner_stats_latest !== undefined &&
      data?.signal_3_partner_stats_latest.length === 0
    ) {
      return {
        partnerHealthLoading: false,
        partnerHealthArray: undefined,
        deployment: data?.deployment,
      };
    }
    return {
      partnerHealthLoading: loading,
      partnerHealthArray:
        data?.signal_3_partner_stats_latest[0].health_score_distribution,
      deployment: data?.deployment,
    };
  }, [data, error, loading]);
};

export const useGetTypicalHealthScoreInRange = (): {
  baselineHealthStatsLoading: boolean;
  baselineHealthStats: Maybe<HealthStatsType>;
  deployment: Deployment;
} => {
  const activePartner = useRecoilValue(activePartnerAtom);
  const { loading, error, data } = useQuery(
    getQueryTypicalHealthScoreInRange(),
    { variables: { partnerId: activePartner?.id } },
  );
  return useMemo(() => {
    if (error) {
      return {
        baselineHealthStatsLoading: false,
        baselineHealthStats: undefined,
        deployment: data?.deployment,
      };
    }
    return {
      baselineHealthStatsLoading: loading,
      baselineHealthStats: data?.baselineHealthScoreBreakdown[0],
      deployment: data?.deployment,
    };
  }, [data, error, loading, activePartner]);
};

export const useGetPartnerHealthScoreInRange = (
  partnerId: string,
): {
  partnerHealthStatsLoading: boolean;
  partnerHealthStats: Maybe<HealthStatsType>;
  deployment: Deployment;
} => {
  const { loading, error, data } = useQuery(
    getQueryPartnerHealthScoreInRange(partnerId),
    { skip: !partnerId },
  );

  return useMemo(() => {
    if (error) {
      return {
        partnerHealthStatsLoading: false,
        partnerHealthStats: undefined,
        deployment: data?.deployment,
      };
    }
    return {
      partnerHealthStatsLoading: loading,
      partnerHealthStats: data?.partnerHealthScoreBreakdown[0],
      deployment: data?.deployment,
    };
  }, [data, error, loading]);
};

export const useGetAverageHealthScoreHero = (): {
  averageHealthScoreHeroLoading: boolean;
  averageHealthScoreArrayHero: any;
  deployment: Deployment;
} => {
  const { loading, error, data } = useQuery(getAverageHealthScoreHeroQuery());

  return useMemo(() => {
    if (error) {
      return {
        averageHealthScoreHeroLoading: false,
        averageHealthScoreArrayHero: [],
        deployment: { name: '' },
      };
    }
    return {
      averageHealthScoreHeroLoading: loading,
      averageHealthScoreArrayHero: data?.signal_3_global_stats_latest,
      deployment: data?.deployment,
    };
  }, [data, error, loading]);
};

export const useGetPartnerLatestStatsHero = (
  partnerId: string,
): {
  partnerLatestStatsHeroLoading: boolean;
  partnerLatestStatsArrayHero: PartnerHeroStats[];
} => {
  const { loading, error, data } = useQuery(
    getPartnerLatestStatsHeroQuery(partnerId),
    { skip: !partnerId },
  );

  return useMemo(() => {
    if (error) {
      return {
        partnerLatestStatsHeroLoading: false,
        partnerLatestStatsArrayHero: [],
        deployment: { name: '' },
      };
    }
    return {
      partnerLatestStatsHeroLoading: loading,
      partnerLatestStatsArrayHero: data?.signal_3_partner_stats_latest || [],
      deployment: data?.signal_3_partner_stats_latest || [],
    };
  }, [data, error, loading]);
};

export const useGetPartnerDailyFactHero = (
  partnerId: string,
  ts: string,
): {
  partnerDailyFactHeroLoading: boolean;
  partnerDailyFactHeroArray: any;
  deployment: Deployment;
} => {
  const { loading, error, data } = useQuery(
    getPartnerDailyFactHeroQuery(partnerId, ts),
    { skip: !partnerId },
  );
  return useMemo(() => {
    if (error) {
      console.error('Error loading partner daily fact hero');
      return {
        partnerDailyFactHeroLoading: false,
        partnerDailyFactHeroArray: [],
        deployment: data?.deployment,
      };
    }
    return {
      partnerDailyFactHeroLoading: loading,
      partnerDailyFactHeroArray: data?.signal_3_partner_stats_daily_fact,
      deployment: data?.deployment,
    };
  }, [data, error, loading]);
};

export const useGetPartnerHealthScoreTimeline = (
  partnerId: string,
  limit: string,
): {
  partnerHealthScoreTimelineLoading: boolean;
  partnerHealthScoreTimelineDataArray: TimelineDataType[];
} => {
  const { loading, error, data } = useQuery(
    limit === '1'
      ? getHourlyPartnerHealthScoreTimelineQuery(partnerId)
      : getPartnerHealthScoreTimelineQuery(partnerId, limit),
    { skip: !partnerId },
  );

  return useMemo(() => {
    if (error) {
      console.error('Error loading partner health score timeline data');
      return {
        partnerHealthScoreTimelineLoading: false,
        partnerHealthScoreTimelineDataArray: [],
      };
    }
    return {
      partnerHealthScoreTimelineLoading: loading,
      partnerHealthScoreTimelineDataArray: data?.healthScoreTimeline || [],
    };
  }, [data, error, loading]);
};

export const useGetTypicalHealthScoreTimeline = (
  limit: string,
): {
  typicalHealthScoreTimelineLoading: boolean;
  typicalHealthScoreTimelineDataArray: TimelineDataType[];
} => {
  const { loading, error, data } = useQuery(
    limit === '1'
      ? getHourlyTypicalHealthScoreTimelineQuery()
      : getTypicalHealthScoreTimelineQuery(limit),
  );
  return useMemo(() => {
    if (error) {
      console.error('Error loading typical health score timeline data');
      return {
        typicalHealthScoreTimelineLoading: false,
        typicalHealthScoreTimelineDataArray: [],
      };
    }
    return {
      typicalHealthScoreTimelineLoading: loading,
      typicalHealthScoreTimelineDataArray: data?.healthScoreTimeline || [],
    };
  }, [data, error, loading]);
};

export const useGetPartnerIssues = (
  partnerId: string,
  limit: string = '1',
  latest: boolean = true,
): {
  partnerIssuesLoading: boolean;
  partnerIssuesData: Maybe<HealthStatsType[]>;
} => {
  const { loading, error, data } = useQuery(
    getPartnerIssuesQuery(partnerId, limit, latest),
    {
      skip: !partnerId,
    },
  );
  return useMemo(() => {
    if (error) {
      console.error('Error loading partner issues');
      return {
        partnerIssuesLoading: false,
        partnerIssuesData: undefined,
      };
    }

    return {
      partnerIssuesLoading: loading,
      partnerIssuesData: data?.partnerIssues || undefined,
    };
  }, [data, error, loading]);
};

export const useGetTypicalIssues = (
  limit: string = '1',
  latest: boolean = true,
): {
  typicalIssuesLoading: boolean;
  typicalIssuesData: Maybe<HealthStatsType[]>;
} => {
  const { loading, error, data } = useQuery(
    getTypicalIssuesQuery(limit, latest),
  );

  return useMemo(() => {
    if (error) {
      return {
        typicalIssuesLoading: false,
        typicalIssuesData: undefined,
      };
    }

    return {
      typicalIssuesLoading: loading,
      typicalIssuesData: data?.typicalIssues || undefined,
    };
  }, [data, error, loading]);
};

export const useGetHealthDownload = (
  partnerId: string,
): {
  downloadLoading: boolean;
  downloadData: any;
  deployment: Deployment;
} => {
  const loadAccountId = isRhoOrCiSignal();
  const { loading, error, data } = useQuery(
    getQueryHealthDownload(partnerId, loadAccountId),
  );

  return useMemo(() => {
    if (error) {
      console.error('Error loading partner daily fact hero');
      return {
        downloadLoading: false,
        downloadData: [],
        deployment: data?.deployment,
      };
    }
    return {
      downloadLoading: loading,
      downloadData: data?.signal_3_location_stats_latest,
      deployment: data?.deployment,
    };
  }, [data, error, loading]);
};

export const useGetIssueMatrix = (
  partnerId: string,
): {
  issueMatrixLoading: boolean;
  issueMatrixData: IssueMatrixStat[];
} => {
  const { loading, error, data } = useQuery(getQueryIssueMatrix(partnerId), {
    skip: partnerId === '' || !partnerId,
  });
  return useMemo(() => {
    if (error) {
      console.error('Error loading partner daily fact hero');
      return {
        issueMatrixLoading: false,
        issueMatrixData: [],
      };
    }
    const issueMatrixData = data?.issueMatrixStats.map(
      (stat: {
        issue_category: any;
        issue_type: any;
        location_count: any;
        non_onboarded_location_count: any;
      }) => ({
        issueCategory: stat.issue_category,
        issueType: stat.issue_type,
        locationCount: stat.location_count,
        nonOnboardedLocationCount: stat.non_onboarded_location_count,
      }),
    );
    return {
      issueMatrixLoading: loading,
      issueMatrixData,
    };
  }, [data, error, loading]);
};
